import { BasePatientDetailsFormFields, CustomerType, FileAccordionData, FileFormFields, FileMapEntry, PatientDetailsFormFields, PatientFileFormFields, StepperFormFields, StepperPatientFormFields, Title } from 'common';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Subject, debounceTime, distinctUntilChanged, map, merge, startWith, takeUntil } from 'rxjs';
import { CaptureDialogFilesAccordionSetup } from '../../../..//utils/constants';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { animate, style, transition, trigger } from '@angular/animations';
import { errorsOf, toTitle } from '../../../../utils/static-functions';
import { FileGroupComponent } from './file-group/file-group.component';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { JsonPipe, NgClass } from '@angular/common';


@Component({
  selector: 'lib-file-accordion',
  standalone: true,
  imports: [NgClass, MatExpansionModule, MatButtonModule, MatIconModule, FileGroupComponent, JsonPipe],
  templateUrl: './file-accordion.component.html',
  styleUrl: './file-accordion.component.scss',
  animations: [
    trigger(
      'fadeInOut',
      [
        transition(
          ':enter', [
          style({ opacity: 0 }),
          animate('0.3s ease-out',
            style({ opacity: 1 }))
        ]),
        transition(
          ':leave', [
          style({ opacity: 1 }),
          animate('0.3s ease-in',
            style({ opacity: 0 }))
        ])
      ]
    )
  ]
})
export class FileAccordionComponent implements OnDestroy, OnInit, OnChanges {

  //#region Variables
  // Variables for this component that are within scope of "this" component.

  /**
   * Whether the component is in read mode.
   */
  @Input({ required: true })
  public isReadMode = false;

  /**
   * Whether the customer is a patient or not.
   */
  @Input({ required: true })
  public isCustomerAPatient!: boolean;

  /**
   * The form group containing the file form fields.
   * Used to update the validity of the form when files are added or removed.
   */
  @Input({ required: true })
  public form!: RelevantFormGroup;

  @Input({ required: true })
  public stepperForm!: FormGroup<StepperFormFields>;

  @Output()
  public validityChanged = new EventEmitter<boolean>();

  /**
   * The file map containing the files for each accordion item.
   * This is used to display the files in the accordion.
   * This is also used to revoke the object URLs when the component is destroyed.
   */
  public fileMap: Record<keyof FileFormFields | keyof PatientFileFormFields, FileMapEntry[]> = {} as any;

  /**
   * The setup for the file accordions  - imported from constants.
   * Used to display the accordions and their contents.
   */
  public customerAccordionSetup: FileAccordionData[] = JSON.parse(JSON.stringify(CaptureDialogFilesAccordionSetup));

  /**
   * The setup for the patient accordions - imported from constants.
   * Used to display the accordions and their contents.
   * The photoPayslipId is removed as it is not required for the patient.
   */
  public patientAccordionSetup: FileAccordionData[] = CaptureDialogFilesAccordionSetup.slice().filter(f => f.formControlNameId !== 'photoPayslipId');

  public areAllFormsValid = false;
  private destroy$ = new Subject<void>();

  //#endregion
  //#region Instantiation
  // Setup\fetches\initialization.

  public ngOnInit(): void {

    // Monitor each form for changes.
    merge(
      this.patientsArray.valueChanges,
      this.customerForm.valueChanges,
      this.customerPayslipForm.valueChanges,
    )
      .pipe(
        startWith(null), // Start with the current value.
        debounceTime(300), // Prevent over-lapping emissions from hindering performance.
        map(() => { // Map to boolean type.
          // Sync changes in the main form with the stepper form.
          this.updateStepperValues();

          // Since validation is done manually "on the fly", we need to check if the form is valid. 
          const isCustomerValid = this.areRequiredCustomerFieldsComplete;
          const arePatientsValid = this.patientsArrayWithoutCustomer.every((p, i) => this.areRequiredPatientFieldsComplete(p, i));
          return isCustomerValid && arePatientsValid;
        }),
        distinctUntilChanged(), // Ignore non-unique value changes.
        takeUntil(this.destroy$)
      )
      .subscribe(isValid => {
        this.validityChanged.emit(isValid);
        this.areAllFormsValid = isValid;

        this.stepperForm.get('allFiles')?.setValue(isValid);
        this.stepperForm.get('allFiles')?.updateValueAndValidity();
        this.stepperForm.updateValueAndValidity();
      });
  }

  //#endregion
  //#region User Interactions
  // Callbacks from html, anything the user can "interact" with from the view.

  //#endregion
  //#region Events and Domain Functions
  // $watches/$on events, functions which don't fall into any of the other regions,
  // including any function that has to do domain functionality.

  public markCustomerFormsTouched() {
    this.customerAccordionSetup.forEach(f => {
      // Mark the actual form controls as touched - this feeds back to the Capture Dialog and contributes to save change detection and confirm close.
      if (this.customerForm.controls) {
        const formControlResource = this.customerForm.controls[f.formControlNameResource as keyof PatientFileFormFields];
        const formControlId = this.customerForm.controls[f.formControlNameId as keyof PatientFileFormFields];
        formControlResource?.markAsTouched();
        formControlResource?.markAsDirty();
        formControlId?.markAsTouched();
        formControlId?.markAsDirty();
      }

      const customerStepperForm = this.stepperForm.controls['customerFiles'];
      if (customerStepperForm) { // Mark the stepper for visual indication.
        customerStepperForm.controls[f.formControlNameResource]?.markAsTouched();
        customerStepperForm.controls[f.formControlNameResource]?.markAsDirty();
        customerStepperForm.controls[f.formControlNameId]?.markAsTouched();
        customerStepperForm.controls[f.formControlNameId]?.markAsDirty();
      }
    });

    // Mark the actual form controls as touched - this feeds back to the Capture Dialog and contributes to save change detection and confirm close.
    if (this.customerPayslipForm.controls) {
      this.customerPayslipForm.controls['photoPayslip'].markAsTouched();
      this.customerPayslipForm.controls['photoPayslip'].markAsDirty();
      this.customerPayslipForm.controls['photoPayslipId'].markAsTouched();
      this.customerPayslipForm.controls['photoPayslipId'].markAsDirty();
    }
  }

  public markPatientFormTouched(patientForm: FormGroup<BasePatientDetailsFormFields | null>) {
    this.patientAccordionSetup.forEach(f => {
      // Mark the actual form controls as touched - this feeds back to the Capture Dialog and contributes to save change detection and confirm close.
      if (patientForm.controls) {
        const formControlResource = patientForm.controls[f.formControlNameResource as keyof PatientFileFormFields];
        const formControlId = patientForm.controls[f.formControlNameId as keyof PatientFileFormFields];
        formControlResource?.markAsTouched();
        formControlResource?.markAsDirty();
        formControlId?.markAsTouched();
        formControlId?.markAsDirty();
      }

      const patientStepperForm = this.getStepperFormOfPatient(patientForm);
      if (patientStepperForm) { // Mark the stepper for visual indication.
        patientStepperForm.controls[f.formControlNameResource]?.markAsTouched();
        patientStepperForm.controls[f.formControlNameResource]?.markAsDirty();
        patientStepperForm.controls[f.formControlNameId]?.markAsTouched();
        patientStepperForm.controls[f.formControlNameId]?.markAsDirty();
      }
    });
  }

  //#endregion
  //#region Data Access & Subscriptions
  // API interactions, NGRX selects, etc.

  //#endregion
  //#region Setters, Updaters and Preloaders
  // Setter functions, and methods called from the Instantiation region.

  /**
   * The stepper form is manually updated after value changes are emitted onto the form.
   * The fields to check from the patient array and customer form are both from the accordion setups.
   */
  private updateStepperValues() {
    // console.log('Issuing update to stepper form fields.');
    // console.log('this.stepperForm:', this.stepperForm.value);

    // Update the customer form.
    if (this.customerForm.controls) {
      const customer = this.customerForm as FormGroup<BasePatientDetailsFormFields>;
      // console.log('customerFiles:', this.stepperForm.controls['customerFiles']);

      // Update the customer files, using patientAccordionSetup to skip the payslip.
      this.patientAccordionSetup.forEach(accord => {
        // console.log('Updating customer form fields.', accord.formControlNameId, accord.formControlNameResource);
        this.stepperForm.controls['customerFiles'].controls[accord.formControlNameId]?.patchValue(customer.controls[accord.formControlNameId as keyof PatientFileFormFields].value as any);
        this.stepperForm.controls['customerFiles'].controls[accord.formControlNameResource]?.patchValue(customer.controls[accord.formControlNameResource as keyof PatientFileFormFields].value as any);
      });

      if (this.customerPayslipForm.controls) {
        this.stepperForm.controls['customerFiles'].controls['photoPayslip']?.patchValue(this.customerPayslipForm.controls['photoPayslip'].value);
        this.stepperForm.controls['customerFiles'].controls['photoPayslipId']?.patchValue(this.customerPayslipForm.controls['photoPayslipId'].value);
      }
    }

    // console.log('patientFiles:', this.stepperForm.controls['patientFiles']);

    // Update the patient forms.
    this.patientsArrayWithoutCustomerOrAuthorizer.forEach((pControl: any, i) => {
      if (pControl.controls) {
        const patient = pControl as FormGroup<BasePatientDetailsFormFields>;
        // console.log(i, 'patient value:', pControl.value);

        // Update the patient files.
        this.patientAccordionSetup.forEach(accord => {
          (this.stepperForm.controls['patientFiles'].at(i) as any).controls[accord.formControlNameId].patchValue(patient.controls[accord.formControlNameId as keyof PatientFileFormFields].value);
          (this.stepperForm.controls['patientFiles'].at(i) as any).controls[accord.formControlNameResource].patchValue(patient.controls[accord.formControlNameResource as keyof PatientFileFormFields].value);
        });
      }
    });
  }

  private updateRequiredFiles(): void {
    // console.log('Updating required files.')
    // 1.8. On document uploads, check that if the customer is not the patient, they are not
    // required to upload a patient card and the patient card will only be required on the patient.
    // Likewise, the patient will be required to have an uploaded patient card, but not an uploaded ID.

    // Mark the patient card as required for the customer if they are a patient.
    this.customerAccordionSetup.find(f => f.formControlNameId === 'photoPatientCardId')!.isRequired = this.isCustomerAPatient;
    // Mark the ID as required for the customer.
    this.customerAccordionSetup.find(f => f.formControlNameId === 'photoIDBookId')!.isRequired = true;
    // Mark the patient card as required for patients.
    this.patientAccordionSetup.find(f => f.formControlNameId === 'photoPatientCardId')!.isRequired = true;
    // Mark the ID as not required for patients.
    this.patientAccordionSetup.find(f => f.formControlNameId === 'photoIDBookId')!.isRequired = false;

    // Reset the stepper forms and validators.
    this.stepperForm.controls['customerFiles'] = new FormGroup<any>({});
    this.stepperForm.controls['patientFiles'] = new FormArray<any>([]);

    // Update the form validators.
    if (this.customerForm.controls) {
      const customer = this.customerForm as FormGroup<BasePatientDetailsFormFields>;

      // Create a new form group from the patient form.
      this.stepperForm.controls['customerFiles'] = new FormGroup<any>({
        id: customer.controls['id'],
        identityNumber: customer.controls['identityNumber'],
        customerType: customer.controls['customerType'],
      });

      this.customerAccordionSetup.forEach(accord => {
        this.stepperForm.controls['customerFiles'].addControl(accord.formControlNameId, new FormControl<any>(null, accord.isRequired ? Validators.required : null));
        this.stepperForm.controls['customerFiles'].addControl(accord.formControlNameResource, new FormControl<any>(null, accord.isRequired ? Validators.required : null));
      });
    }

    this.patientsArrayWithoutCustomerOrAuthorizer.forEach((pControl: any) => {
      if (pControl.controls) {
        const patient = pControl as FormGroup<BasePatientDetailsFormFields>;
        // Create a new form group from the patient form.
        const patentFileForm = new FormGroup<any>({
          id: patient.controls['id'],
          identityNumber: patient.controls['identityNumber'],
          customerType: patient.controls['customerType'],
        });

        this.patientAccordionSetup.forEach(accord => {
          patentFileForm.addControl(accord.formControlNameId, new FormControl<any>(null, accord.isRequired ? Validators.required : null));
          patentFileForm.addControl(accord.formControlNameResource, new FormControl<any>(null, accord.isRequired ? Validators.required : null));
        });

        // Add the patient file form to the stepper form.
        this.stepperForm.controls['patientFiles'].push(patentFileForm);
      }
    });

    this.stepperForm.updateValueAndValidity();
    this.stepperForm.controls['patientFiles'].updateValueAndValidity();
    this.stepperForm.controls['customerFiles'].updateValueAndValidity();
  }

  /**
   * Returns a key-value pair of errors for the given control.
   * key = form control name.
   * value = error object.
   */
  public errorsOf(control: FormGroup | FormControl | null): any {
    return errorsOf(control);
  }

  //#endregion
  //#region Getters and Filters
  // Getter functions and filter methods/functions.

  /**
   * Returns the patients from the form.
   *
   * @readonly
   */
  public get patientsArray() {
    return this.form.controls['patients'] as unknown as FormArray<FormGroup<BasePatientDetailsFormFields | null>>;
  }

  public get patientsStepperArray() {
    return this.stepperForm.controls['patientFiles'] as unknown as FormArray<FormGroup<BasePatientDetailsFormFields | null>>;
  }

  get patientsArrayWithoutCustomer() {
    const allPatients = this.form.controls["patients"];
    const controls = allPatients.controls;

    return controls.filter((c, i) => i !== 0) as unknown as FormGroup<BasePatientDetailsFormFields | null>[];
  }

  get patientsArrayWithoutCustomerOrAuthorizer() {
    const allPatients = this.form.controls["patients"];
    const controls = allPatients.controls;

    return controls.filter((c, i) => i !== 0 && (c.value as any)?.customerType !== CustomerType.Authorizer) as FormControl<BasePatientDetailsFormFields | null>[];
  }

  public get customerForm() {
    return this.form.controls['customerInformation'] as unknown as FormGroup<BasePatientDetailsFormFields | null>;
  }

  public get customerPayslipForm() {
    return this.form.controls['files'] as FormGroup<FileFormFields | null>;
  }

  public getStepperFormOfPatient(patientForm: FormGroup<BasePatientDetailsFormFields | null>) {
    return this.stepperForm.controls['patientFiles'].controls.find(c => c.value.id === patientForm.value?.id);
  }

  //#endregion
  //#region Change Detection
  // Deep config comparisons, change log generation, hasChangesFn, etc.

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['isCustomerAPatient']) {
      setTimeout(() => {
        this.updateRequiredFiles();
      });
    }
  }

  //#endregion    
  //#region Parsing and Validation
  // Transformation/mapping methods
  // Methods returning true/false: isCurrent, areFeedsEqual, shouldProcess, etc.

  public toTitle(title?: Title | null) {
    return toTitle(title)
  }

  /**
   * Required fields are:
   * - photoIDBook
   * - photoIDBookId
   * - photoPatientCardId
   * - photoPatientCard
   * 
   * @see CaptureDialogFilesAccordionSetup in constants.ts
   * @readonly
   */
  public get areRequiredCustomerFieldsComplete() {
    const result = this.areRequiredFileFieldsComplete(this.stepperForm.controls['customerFiles'], true);
    return result;
  }

  public get hasTouchedRequiredCustomerFields() {
    const result = this.areRequiredFileFieldsTouched(this.stepperForm.controls['customerFiles'], true);
    return result;
  }

  public get areRequiredCustomerFieldsDirty() {
    const result = this.areRequiredFileFieldsDirty(this.stepperForm.controls['customerFiles'], true);
    return result;
  }

  public areRequiredPatientFieldsComplete(patientForm: FormGroup<BasePatientDetailsFormFields | null>, index: number) {
    const patientStepperForm = this.stepperForm.controls['patientFiles'].controls.find(c => c.value.id === patientForm.value?.id);
    if (!patientStepperForm) return false;
    return this.areRequiredFileFieldsComplete(patientStepperForm, false);
  }

  public hasTouchedRequiredPatientFields(patientForm: FormGroup<BasePatientDetailsFormFields | null>, index: number) {
    const patientStepperForm = this.stepperForm.controls['patientFiles'].controls.find(c => c.value.id === patientForm.value?.id);
    if (!patientStepperForm) return false;
    return this.areRequiredFileFieldsTouched(patientStepperForm, false);
  }

  public areRequiredPatientFieldsDirty(patientForm: FormGroup<BasePatientDetailsFormFields | null>, index: number) {
    const patientStepperForm = this.stepperForm.controls['patientFiles'].controls.find(c => c.value.id === patientForm.value?.id);
    if (!patientStepperForm) return false;
    return this.areRequiredFileFieldsDirty(patientStepperForm, false);
  }

  private areRequiredFileFieldsComplete(form: FormGroup<StepperPatientFormFields> | FormGroup<StepperPatientFormFields | null>, isCustomer: boolean) {
    return (isCustomer ? this.customerAccordionSetup : this.patientAccordionSetup).every(f => {
      if (form.controls) {
        // If the field is not required, return true.
        if (f.isRequired === false) return true;

        // Check if the field is complete.
        const formControlResource = form.controls[f.formControlNameResource as keyof PatientFileFormFields];
        const formControlId = form.controls[f.formControlNameId as keyof PatientFileFormFields];
        return (formControlResource?.value || null) !== null && (formControlId?.value || null) !== null;
      }
      return false;
    });
  }

  private areRequiredFileFieldsTouched(form: FormGroup<StepperPatientFormFields> | FormGroup<StepperPatientFormFields | null>, isCustomer: boolean) {
    return (isCustomer ? this.customerAccordionSetup : this.patientAccordionSetup).every(f => {
      if (form.controls) {
        // If the field is not required, return true.
        if (f.isRequired === false) return true;

        const formControlResource = form.controls[f.formControlNameResource as keyof PatientFileFormFields];
        const formControlId = form.controls[f.formControlNameId as keyof PatientFileFormFields];
        return (formControlResource?.touched || false) || (formControlId?.touched || false);
      }
      return false;
    });
  }

  private areRequiredFileFieldsDirty(form: FormGroup<StepperPatientFormFields> | FormGroup<StepperPatientFormFields | null>, isCustomer: boolean) {
    return (isCustomer ? this.customerAccordionSetup : this.patientAccordionSetup).some(f => {
      if (form.controls) {
        // If the field is not required, return true.
        if (f.isRequired === false) return true;

        const formControlResource = form.controls[f.formControlNameResource as keyof PatientFileFormFields];
        const formControlId = form.controls[f.formControlNameId as keyof PatientFileFormFields];
        return (formControlResource?.dirty || false) || (formControlId?.dirty || false);
      }
      return false;
    });
  }

  //#endregion
  //#region Response Handlers
  // API/NGRX callback handlers.

  //#endregion
  //#region Navigation
  // Confirmation close dialogs, router guards, etc.

  //#endregion
  //#region Post Instantiation
  // Post-init fetch requests, used in such as landing page, after view init, etc.

  //#endregion

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    // Revoke all object URLs.
    Object.values(this.fileMap).forEach(files => {
      files.forEach(f => {
        if (f.localUrl) URL.revokeObjectURL(f.localUrl);
      });
    });
  }

}

/**
 * The relevant fields for the signing process.
 * ALL fields on the form from the capture dialog are present as this is the master form.
 * The reduction of fields is to narrow interest and help prevent errors.
 */
interface RelevantFormGroup extends FormGroup<any> {
  controls: {
    /**
     * Array of each patient's details.
     */
    patients: FormArray<FormControl<BasePatientDetailsFormFields | null>>,

    /**
     * The customer's information.
     */
    customerInformation: FormGroup<PatientDetailsFormFields>

    files: FormGroup<FileFormFields>,
  }
}